<template>
  <div class="chat-area">
    <!-- 头部 -->
    <div class="chat-header">
      <el-button
        v-if="showSidebarToggle"
        type="text"
        icon="el-icon-s-fold"
        @click="toggleSidebar"
        class="sidebar-toggle"
      />
      <div class="conversation-title" v-if="activeConversation">
        <span>{{ activeConversation ? (activeConversation.customerName ): "请选择对话" }}</span>
        <span style="display: block;">{{ '地址：'+(activeConversation.customerLocation ? activeConversation.customerLocation :'--')+ '    IP：'+(activeConversation.customerIp ? activeConversation.customerIp:'--')  }}</span>
      </div>
      
      <!-- 当前聊天搜索框 -->
      <div class="current-chat-search" v-if="activeConversation">
        <el-input
          v-model="currentSearchKeyword"
          placeholder="在当前对话中搜索..."
          prefix-icon="el-icon-search"
          clearable
          size="small"
          @keyup.enter.native="performCurrentChatSearch"
          @clear="clearCurrentChatSearch"
          @focus="showSearchResults = true"
          style="width: 200px; margin-right: 10px;"
        >
          <template #append>
            <el-button 
              icon="el-icon-search" 
              size="small"
              :loading="currentSearching"
              @click="performCurrentChatSearch"
            />
          </template>
        </el-input>
        
        <!-- 搜索结果导航 -->
        <div v-if="showSearchResults && currentSearchResults.length > 0" class="search-navigation">
          <span class="search-count">{{ currentSearchIndex + 1 }}/{{ currentSearchResults.length }}</span>
          <el-button 
            type="text" 
            icon="el-icon-arrow-up" 
            size="mini"
            :disabled="currentSearchIndex <= 0"
            @click="navigateToPreviousResult"
            title="上一个"
          />
          <el-button 
            type="text" 
            icon="el-icon-arrow-down" 
            size="mini"
            :disabled="currentSearchIndex >= currentSearchResults.length - 1"
            @click="navigateToNextResult"
            title="下一个"
          />
          <el-button 
            type="text" 
            icon="el-icon-close" 
            size="mini"
            @click="clearCurrentChatSearch"
            title="关闭搜索"
          />
        </div>
      </div>
      
      <el-button
        type="text"
        icon="el-icon-switch-button"
        @click="handleLogout"
        class="logout-btn"
      >
        退出登录
      </el-button>
    </div>

    <!-- 消息区域 -->
    <div
      class="messages-container"
      ref="messagesContainer"
      v-loading="loadingMessages"
    >
      <div v-if="!activeConversation" class="no-conversation">
        <el-empty description="请选择一个对话开始聊天" />
      </div>

      <div v-else>
        <!-- 加载更多消息指示器 -->
        <div v-if="loadingMoreMessages" class="loading-more-indicator">
          <i class="el-icon-loading"></i>
          <span>正在加载更多消息...</span>
        </div>

        <MessageItem
          v-for="message in messages"
          :key="message.id"
          :message="message"
          :conversation="activeConversation"
          @recall-message="handleRecallMessage"
          @scrollToBottom="scrollToBottom"
        />
      </div>
    </div>

    <!-- 输入区域 -->
    <!-- 输入区域 -->
    <div v-if="activeConversation" class="message-input-area">
      <div class="input-container">
        <!-- 工具栏 -->
        <div class="input-tools">
          <el-upload
            ref="imageUpload"
            action=""
            :auto-upload="false"
            :show-file-list="false"
            accept="image/*"
            :on-change="handleImageSelect"
          >
            <el-button
              type="text"
              icon="el-icon-picture"
              size="small"
              class="tool-button"
              title="发送图片"
            />
          </el-upload>

          <el-upload
            ref="videoUpload"
            action=""
            :auto-upload="false"
            :show-file-list="false"
            accept="video/*"
            :on-change="handleVideoSelect"
          >
            <el-button
              type="text"
              icon="el-icon-video-camera"
              size="small"
              class="tool-button"
              title="发送视频"
            />
          </el-upload>

          <!-- 黑名单相关按钮 -->
          <!-- 拉黑用户按钮 - 用户未被拉黑时显示 -->
          <el-button
            v-if="!activeConversation.isBlacklisted"
            type="text"
            size="small"
            class="tool-button"
            title="拉黑用户"
            @click="handleBlacklistUser"
          >
            <i class="el-icon-delete"></i>
            拉黑用户
          </el-button>
          
          <!-- 解除拉黑按钮 - 用户已被拉黑时显示 -->
          <el-button
            v-if="activeConversation.isBlacklisted"
            type="text"
            size="small"
            class="tool-button"
            title="解除拉黑"
            @click="handleUnblacklistUser"
          >
            <i class="el-icon-success"></i>
            解除拉黑
          </el-button>
          
          <el-button
            type="text"
            size="small"
            class="tool-button"
            title="查看黑名单"
            @click="handleViewBlacklist"
          >
            <i class="el-icon-document-copy"></i>
            查看黑名单
          </el-button>

          <!-- <el-upload
            ref="fileUpload"
            action=""
            :auto-upload="false"
            :show-file-list="false"
            :on-change="handleFileSelect"
          >
            <el-button
              type="text"
              icon="el-icon-document"
              size="small"
              class="tool-button"
              title="发送文件"
            />
          </el-upload> -->
        </div>

        <!-- 输入框容器 -->
        <div class="input-wrapper">
          <el-input
            v-model="messageText"
            placeholder="请输入消息...（Shift+Enter 可换行，Enter 发送）"
            :disabled="sending"
            @keydown.native="handleKeyDownNative"
            ref="messageInput"
            class="message-input"
            type="textarea"
            :rows="1"
            :autosize="{ minRows: 1, maxRows: 5 }"
          />
          <el-button
            type="primary"
            :loading="sending"
            :disabled="!messageText.trim()"
            @click="sendMessage"
            class="send-button"
            size="small"
          >
            <i class="el-icon-s-promotion"></i>
          </el-button>
        </div>
      </div>
    </div>

    <!-- 隐藏的音频元素用于播放通知声音 -->
    <audio ref="notificationAudio" preload="auto"></audio>

    <!-- 黑名单对话框 -->
    <el-dialog 
      title="黑名单列表" 
      :visible.sync="blacklistDialogVisible"
      width="70%"
      @close="blacklistDialogVisible = false"
    >
      <el-table
        :data="blacklistData"
        v-loading="blacklistLoading"
        style="width: 100%"
        max-height="500"
      >
        <el-table-column prop="userName" label="用户名" width="180" />
        <el-table-column prop="userId" label="用户ID" width="150" />
        <el-table-column prop="ipAddress" label="IP地址" width="150" />
        <el-table-column prop="reason" label="封禁原因" />
        <el-table-column prop="createTime" label="创建时间" width="180" />
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="text"
              size="small"
              @click="removeFromBlacklist(scope.row.userId)"
            >
              解除拉黑
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <el-pagination
        @size-change="handleBlacklistPageSizeChange"
        @current-change="handleBlacklistPageChange"
        :current-page="blacklistPageNum"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="blacklistPageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="blacklistTotal"
        style="margin-top: 20px; text-align: right;"
      />
    </el-dialog>
  </div>
</template>

<script>
import MessageItem from "./MessageItem.vue";
import { decryptAES,isBase64String } from '@/utils/crypto'
import { formatBeijingTime } from '@/utils/time'
export default {
  name: "ChatArea",
  components: {
    MessageItem,
  },
  props: {
    showSidebarToggle: {
      type: Boolean,
      default: false,
    },
    staffStatus: {
      type: String,
      default: "online", // online|busy
    },
  },
  data() {
    return {
      messageText: "",
      sending: false,
      loadingMessages: false,
      loadingMoreMessages: false, // 加载更多消息中
      hasMoreMessages: true, // 是否还有更多消息
      currentPage: 1, // 当前页码
      pageSize: 1000, // 每页大小
      
      // 当前聊天搜索相关
      currentSearchKeyword: "",
      currentSearching: false,
      showSearchResults: false,
      currentSearchResults: [],
      currentSearchIndex: -1,

      // 黑名单相关
      blacklistDialogVisible: false,
      blacklistLoading: false,
      blacklistData: [],
      blacklistPageNum: 1,
      blacklistPageSize: 20,
      blacklistTotal: 0,
    };
  },
  computed: {
    activeConversation() {
      return this.$store.getters.activeConversation;
    },
    messages() {
      const conversationId = this.$store.state.activeConversationId;
    
      const messages = this.$store.getters.conversationMessages(conversationId);
      console.log(
        `获取消息，对话ID: ${conversationId}, 消息数量: ${messages.length}`
      );
      console.log("store.messages:", this.$store.state.messages);
      console.log(
        "store.messages[conversationId]:",
        this.$store.state.messages[conversationId]
      );
      return messages;
    },
  },
  watch: {
    activeConversation: {
      handler(newConv, oldConv) {
        // 只在对话ID真正改变时才加载消息，避免不必要的重复加载
        
        if (newConv && (!oldConv || newConv.customId !== oldConv.customId)) {
          console.log(
            "对话切换:",
            oldConv ? oldConv.id : "null",
            "->",
            newConv.id
          );
          this.loadMessages();
        }
      },
      immediate: true,
    },
  },
  methods: {
    // 自动重连 WebSocket
    async ensureWebSocketConnected() {
      // 检查是否已连接
      if (window.wsService && window.wsService.isConnected) {
        return true;
      }

      // 显示未连接提示
      this.$message.error("未连接到服务器");

      // 等待1秒后提示重连
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 显示重连提示
      const loadingInstance = this.$message({
        message: "系统尝试给你自动重连，重连中...",
        type: 'info',
        duration: 0,
        iconClass: 'el-icon-loading'
      });

      try {
        // 从本地存储获取 auth_token
        const authToken = localStorage.getItem('auth_token');
        if (!authToken) {
          loadingInstance.close();
          this.$message.error("身份验证信息丢失，请重新登录");
          return false;
        }

        // 进行重连
        await window.wsService.connect(authToken, true);
        
        // 关闭 loading 提示
        loadingInstance.close();
        
        // 重连成功提示
        this.$message.success("重连成功");
        return true;
      } catch (error) {
        console.error("WebSocket 重连失败:", error);
        loadingInstance.close();
        this.$message.error("重连失败，请稍后重试或尝试刷新");
        return false;
      }
    },

    // 播放通知声音
    playNotificationSound() {
      try {
        // 检查全局声音设置
        const globalSoundEnabled = localStorage.getItem('globalSoundEnabled') !== 'false';
        console.log('检查声音设置:', globalSoundEnabled);
        if (!globalSoundEnabled) {
          console.log('声音已禁用');
          return;
        }

        // 使用 Web Audio API 生成"滴滴滴"声音
        let audioContext = window.audioContext;
        
        // 如果 audioContext 还没有创建，则创建
        if (!audioContext) {
          audioContext = new (window.AudioContext || window.webkitAudioContext)();
          window.audioContext = audioContext;
          console.log('创建新的 AudioContext');
        }
        
        // 立即播放声音，不等待 AudioContext 恢复
        // 如果 AudioContext 是 suspended 状态，尝试恢复并播放
        if (audioContext.state === 'suspended') {
          console.log('AudioContext 处于 suspended 状态，立即尝试恢复并播放');
          audioContext.resume().then(() => {
            console.log('AudioContext 恢复成功，立即播放声音');
            this._playBeep(audioContext);
          }).catch(err => {
            console.error('恢复 AudioContext 失败:', err);
            // 即使恢复失败也尝试播放，有些浏览器可能允许
            try {
              this._playBeep(audioContext);
            } catch (playError) {
              console.error('播放声音失败:', playError);
            }
          });
        } else {
          // AudioContext 已经是 running 状态，立即播放
          this._playBeep(audioContext);
        }
      } catch (error) {
        console.error('播放通知声音失败:', error);
      }
    },

    // 实际播放声音的方法
    _playBeep(audioContext) {
      const now = audioContext.currentTime;
      
      // 创建振荡器和增益节点
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // 设置频率 (800 Hz)
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      // 播放3次"滴"声，每次100ms，间隔200ms
      const beepDuration = 0.1; // 100ms
      const beepInterval = 0.2; // 200ms
      
      gainNode.gain.setValueAtTime(0, now);
      
      for (let i = 0; i < 3; i++) {
        const startTime = now + i * beepInterval;
        const endTime = startTime + beepDuration;
        
        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.setValueAtTime(0, endTime);
      }
      
      oscillator.start(now);
      // 计算总的持续时间
      const totalDuration = 2 * beepInterval + beepDuration;
      oscillator.stop(now + totalDuration);
      
      console.log('声音播放已启动');
    },

    // 初始化 AudioContext
    initAudioContext() {
      try {
        if (!window.audioContext) {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          window.audioContext = audioContext;
          console.log('AudioContext 已初始化');
          
          // 如果 AudioContext 处于 suspended 状态，添加一个点击事件监听器来恢复它
          if (audioContext.state === 'suspended') {
            const resume = () => {
              audioContext.resume().then(() => {
                console.log('AudioContext 已恢复');
                document.removeEventListener('click', resume);
                document.removeEventListener('keydown', resume);
                document.removeEventListener('touchstart', resume);
              });
            };
            
            document.addEventListener('click', resume);
            document.addEventListener('keydown', resume);
            document.addEventListener('touchstart', resume);
          }
        }
      } catch (error) {
        console.error('初始化 AudioContext 失败:', error);
      }
    },

    toggleSidebar() {
      this.$store.commit(
        "SET_SIDEBAR_COLLAPSED",
        !this.$store.state.sidebarCollapsed
      );
    },
    async loadMessages() {
      // 重置分页状态
      this.currentPage = 1;
      this.hasMoreMessages = true;

      // 调用 store 的 loadMessages 方法
      if (this.activeConversation) {
        await this.$store.dispatch("loadMessages", {
          conversationId: this.$store.state.activeConversationId,
          conversation: this.activeConversation,
          pageNum: this.currentPage,
          pageSize: this.pageSize,
        });
        this.$nextTick(() => {

          this.scrollToBottom();
        });
      }
    },
    // 加载更多历史消息
    async loadMoreMessages() {
      if (
        this.loadingMoreMessages ||
        !this.hasMoreMessages ||
        !this.activeConversation
      ) {
        return;
      }

      this.loadingMoreMessages = true;
      const nextPage = this.currentPage + 1;

      try {
        // 记录当前滚动位置
        const container = this.$refs.messagesContainer;
        const oldScrollHeight = container.scrollHeight;

        console.log(this.activeConversation, "activeConversation");

        await this.$store.dispatch("loadMessages", {
          conversationId: this.$store.state.activeConversationId,
          conversation: this.activeConversation,
          pageNum: nextPage,
          pageSize: this.pageSize,
          prepend: true,
        });

        // 检查是否还有更多消息
        const messages = this.messages;
        if (messages.length < nextPage * this.pageSize) {
          this.hasMoreMessages = false;
        } else {
          this.currentPage = nextPage;
        }

        // 恢复滚动位置
        this.$nextTick(() => {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = newScrollHeight - oldScrollHeight;
        });
      } catch (error) {
        console.error("加载更多消息失败:", error);
        this.$message.error("加载更多消息失败");
      } finally {
        this.loadingMoreMessages = false;
      }
    },

    sendMessageByOther(message) {
          // 先在本地添加消息到UI
          const message2 = {
          id: message.messageId, // 前端生成的UUID
          serverId: null, // 后端ID，发送成功后会更新
          content: message.content,
          type: 1, // 文本消息
          sender: message.senderId,
          receiver: message.receiverId,
          createTime: formatBeijingTime(), // 使用北京时间
          isOwn: true,
          status: "sending",
        };

         // 添加到store
         this.$store.commit("ADD_MESSAGE", {
          conversationId: this.$store.state.activeConversationId,
          message: message2,
        });
        message.status = "sent";
          this.$nextTick(() => {
            this.scrollToBottom();
            this.$refs.messageInput.focus();
          });
    },
    async sendMessage() {
      console.log("sendMessage called", {
        messageText: this.messageText,
        trimmed: this.messageText && this.messageText.trim(),
        activeConversationId: this.$store.state.activeConversationId,
        staffStatus: this.staffStatus,
        sending: this.sending,
        wsConnected: window.wsService && window.wsService.isConnected,
      });
      console.log(this.activeConversation, "this.activeConversation");
      
      if (this.staffStatus === 'busy') {
        console.log("sendMessage blocked: staffStatus busy");
        this.$message.warning('当前处于忙碌状态，无法发送消息。切换到上线状态后再试。');
        return;
      }
      
      if (!this.messageText.trim() || !this.activeConversation || this.sending){
        console.log("发送消息被阻止，条件不满足");
        console.log("messageText:", this.messageText);
        console.log("activeConversation:", this.activeConversation);
        console.log("sending:", this.sending);
        return
      }
        

      if (!window.wsService || !window.wsService.isConnected) {
        console.log("sendMessage wsService not connected, trying reconnect");
        const connected = await this.ensureWebSocketConnected();
        console.log("sendMessage reconnect result", { connected });
        if (!connected) {
          console.log("sendMessage blocked: ws not connected");
          return;
        }
      }

      const text = this.messageText.trim();
      const messageId = Date.now().toString();

      // 先清空输入框
      this.messageText = "";
      this.sending = true;

      try {
        // 先在本地添加消息到UI
        const message = {
          id: messageId, // 前端生成的UUID
          serverId: null, // 后端ID，发送成功后会更新
          content: text,
          type: 1, // 文本消息
          sender: this.activeConversation.senderAccount,
          receiver: this.activeConversation.customerId,
          createTime: formatBeijingTime(), // 使用北京时间
          isOwn: true,
          status: "sending",
        };

        // 添加到store
        this.$store.commit("ADD_MESSAGE", {
          conversationId: this.$store.state.activeConversationId,
          message: message,
        });

        // 使用WebSocket发送消息
        const receiverId = this.activeConversation.customerId;
        const success = window.wsService.sendMessage(

          text,
          receiverId,
          this.activeConversation.senderAccount,
          messageId
        );

        if (success) {
          // 更新消息状态为已发送
          message.status = "sent";
          this.$nextTick(() => {
            this.scrollToBottom();
            this.$refs.messageInput.focus();
          });
        } else {
          // 发送失败
          message.status = "failed";
          this.$message.error("发送失败");
          this.messageText = text; // 恢复输入内容
        }
      } catch (error) {
        console.error("Failed to send message:", error);
        this.$message.error("发送失败");
        this.messageText = text; // 恢复输入内容
      } finally {
        this.sending = false;
      }
    },
    async handleImageSelect(file) {
      try {
        const formData = new FormData();
        formData.append("file", file.raw);

        const response = await this.$http.post(
          "/api/message/upload",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            timeout: 0,
          }
        );

        console.log("上传图片响应:", response);

        if (response.data && response.data.code === 200) {
          // const imageUrl = response.data.data;
          const imageUrl =isBase64String(response.data.data) ? decryptAES(response.data.data) : response.data.data || "";
          // 检查WebSocket连接
          if (!window.wsService || !window.wsService.isConnected) {
            // 尝试自动重连
            const connected = await this.ensureWebSocketConnected();
            if (!connected) {
              return;
            }
          }

          // 先在本地添加图片消息到UI
          const messageId = Date.now().toString();
          const message = {
            id: messageId, // 前端生成的UUID
            serverId: null, // 后端ID，发送成功后会更新
            content: imageUrl,
            type: 2, // 图片消息
            sender: this.activeConversation.senderAccount,
            receiver: this.activeConversation.customerId,
            timestamp: new Date().toISOString(),
            createTime: formatBeijingTime(), // 使用北京时间
            isOwn: true,
            status: "sending",
          };

          //添加到store
          this.$store.commit("ADD_MESSAGE", {
            conversationId: this.$store.state.activeConversationId,
            message: message,
          });

          // 使用WebSocket发送图片消息
          const receiverId = this.activeConversation.customerId;
          const success = window.wsService.sendImageMessage(
            imageUrl,
            receiverId,
            this.activeConversation.senderAccount,
            messageId
          );

          if (success) {
            message.status = "sent";
            this.$message.success("图片发送成功");
            this.scrollToBottom();
          } else {
            message.status = "failed";
            this.$message.error("图片发送失败");
          }
        } else {
          throw new Error(response.data.msg || "上传失败");
        }
      } catch (error) {
        console.error("Failed to upload image:", error);
        this.$message.error("图片上传失败");
      }
    },
    async handleVideoSelect(file) {
      try {
        const maxSize = 100 * 1024 * 1024; // 100MB
        if (file.raw.size > maxSize) {
          this.$message.error("视频大小不能超过 100MB");
          return;
        }

        if (!file.raw.type.startsWith("video/")) {
          this.$message.error("请选择视频文件");
          return;
        }

        const formData = new FormData();
        formData.append("file", file.raw);

        const loading = this.$loading({
          lock: true,
          text: "正在上传视频...",
          spinner: "el-icon-loading",
          background: "rgba(0, 0, 0, 0.7)",
        });

        const response = await this.$http.post(
          "/api/message/upload",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            timeout: 0,
          }
        );

        loading.close();

        if (response.data && response.data.code === 200) {
          const videoUrl = isBase64String(response.data.data) ? decryptAES(response.data.data) : response.data.data || "";
          
          if (!window.wsService || !window.wsService.isConnected) {
            const connected = await this.ensureWebSocketConnected();
            if (!connected) return;
          }

          const messageId = Date.now().toString();
          const message = {
            id: messageId,
            serverId: null,
            content: videoUrl,
            type: 5, // 视频
            sender: this.activeConversation.senderAccount,
            receiver: this.activeConversation.customerId,
            timestamp: new Date().toISOString(),
            createTime: formatBeijingTime(),
            isOwn: true,
            status: "sending",
          };

          this.$store.commit("ADD_MESSAGE", {
            conversationId: this.$store.state.activeConversationId,
            message: message,
          });

          const receiverId = this.activeConversation.customerId;
          const success = window.wsService.sendVideoMessage(
            videoUrl,
            receiverId,
            this.activeConversation.senderAccount,
            messageId
          );

          if (success) {
            message.status = "sent";
            this.$message.success("视频发送成功");
            this.scrollToBottom();
          } else {
            message.status = "failed";
            this.$message.error("视频发送失败");
          }
        } else {
          throw new Error(response.data.msg || "上传失败");
        }
      } catch (error) {
        console.error("Failed to upload video:", error);
        this.$message.error("视频上传失败");
      }
    },
    async handleFileSelect(file) {
      try {
        const formData = new FormData();
        formData.append("file", file.raw);

        const response = await this.$http.post(
          "/api/message/upload",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            timeout: 0,
          }
        );

        if (response.data && response.data.code === 200) {
          //const fileUrl = response.data.data;
          const fileUrl =isBase64String(response.data.data) ? decryptAES(response.data.data) : response.data.data || "";
          // 检查WebSocket连接
          if (!window.wsService || !window.wsService.isConnected) {
            // 尝试自动重连
            const connected = await this.ensureWebSocketConnected();
            if (!connected) {
              return;
            }
          }

          const fileInfo = {
            url: fileUrl,
            name: file.name,
            size: file.size,
            type: file.type,
            senderAccount: this.activeConversation.senderAccount,
          };

          const messageId = Date.now().toString();

          // 先在本地添加文件消息到UI
          const message = {
            id: messageId, // 前端生成的UUID
            serverId: null, // 后端ID，发送成功后会更新
            content: fileUrl,
            type: 3, // 文件消息
            sender: this.activeConversation.senderAccount,
            receiver: this.activeConversation.customerId,
            timestamp: new Date().toISOString(),
            isOwn: true,
            status: "sending",
            extra: {
              name: file.name,
              size: file.size,
            },
          };

          // 添加到store
          this.$store.commit("ADD_MESSAGE", {
            conversationId: this.$store.state.activeConversationId,
            message: message,
          });

          // 使用WebSocket发送文件消息
          const receiverId = this.activeConversation.customerId;
          const success = window.wsService.sendFileMessage(
            fileInfo,
            receiverId,
            messageId
          );

          if (success) {
            message.status = "sent";
            this.$message.success("文件发送成功");
            this.scrollToBottom();
          } else {
            message.status = "failed";
            this.$message.error("文件发送失败");
          }
        } else {
          throw new Error(response.data.msg || "上传失败");
        }
      } catch (error) {
        console.error("Failed to upload file:", error);
        this.$message.error("文件上传失败");
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.messagesContainer) {
          this.$refs.messagesContainer.scrollTop =
            this.$refs.messagesContainer.scrollHeight;
        }
      });
    },
    // 处理撤回消息请求
    handleRecallMessage(messageId) {
      this.$confirm("确定要撤回这条消息吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          this.recallMessage(messageId);
        })
        .catch(() => {
          // 用户取消操作
        });
    },
    // 撤回消息
    async recallMessage(messageId) {
      try {
        const response = await this.$http.post("/api/message/recall", {
          messageId: messageId,
        });

        if (response.data && response.data.code === 200) {
          // 更新本地消息状态
          this.$store.commit("RECALL_MESSAGE", {
            conversationId: this.$store.state.activeConversationId,
            messageId: messageId,
          });

          this.$message.success("消息已撤回");
        } else {
          throw new Error((response.data && response.data.msg) || "撤回失败");
        }
      } catch (error) {
        console.error("撤回消息失败:", error);
        this.$message.error("撤回消息失败");
      }
    },
    handleLogout() {
      this.$confirm("确定要退出登录吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }).then(() => {
        this.$store.dispatch("logout");
        this.$router.push("/login");
        if (window.wsService) {
          window.wsService.disconnect();
       }
      console.log("用户已退出登录",window.wsService);
      });
    },
    // 添加滚动事件监听器
    addScrollListener() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.addEventListener("scroll", this.handleScroll);
      }
    },
    // 移除滚动事件监听器
    removeScrollListener() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.removeEventListener("scroll", this.handleScroll);
      }
    },
    // 处理滚动事件
    handleScroll() {
      const container = this.$refs.messagesContainer;
      if (container) {
        // 当滚动到顶部50px范围内时，触发加载更多消息
        if (
          container.scrollTop <= 50 &&
          !this.loadingMoreMessages &&
          this.hasMoreMessages
        ) {
          this.loadMoreMessages();
        }
      }
    },

    // ========== 当前聊天搜索相关方法 ==========
    
    // 执行当前聊天搜索
    async performCurrentChatSearch() {
      const keyword = this.currentSearchKeyword.trim();
      if (!keyword) {
        this.$message.warning("请输入搜索关键词");
        return;
      }

      this.currentSearching = true;
      this.currentSearchResults = [];
      this.currentSearchIndex = -1;

      try {
        // 调用搜索API - 当前聊天搜索时传sessionId
        const sessionId = this.activeConversation && this.activeConversation.id;
        if (!sessionId) {
          this.$message.warning("请先选择一个对话");
          return;
        }

        const response = await this.$http.get('/api/message/search', {
          params: {
            sessionId: sessionId,
            keyword: keyword,
            pageNum: 1,
            pageSize: 50
          }
        });

        if (response.data && response.data.code === 200) {
           const data = isBase64String(response.data.data) 
            ? decryptAES(response.data.data) 
            : response.data.data;
            console.log(data,11111);
            
          const records = data.records || [];
          
          // 转换消息格式
          const results = records.map(record => ({
            id: record.messageId,
            serverId: record.id,
            content: record.content,
            type: record.messageType,
            sender: record.senderId,
            receiver: record.receiverId,
            createTime: record.createTime,
            senderName: record.senderName,
            senderAvatar: record.senderAvatar,
            receiverName: record.receiverName,
            receiverAvatar: record.receiverAvatar,
            isRead: record.readStatus === 1 || record.isRead === 1,
            isRevoked: record.isRevoked === 1
          }));

          this.currentSearchResults = results;
          this.showSearchResults = true;

          if (results.length === 0) {
            this.$message.info("没有找到相关消息");
          } else {
            // 自动跳转到第一个结果
            this.currentSearchIndex = 0;
            this.scrollToSearchResult(results[0]);
          }
        } else {
          throw new Error(response.data.msg || "搜索失败");
        }
      } catch (error) {
        console.error("Current chat search failed:", error);
        this.$message.error("搜索失败");
      } finally {
        this.currentSearching = false;
      }
    },

    // 清除当前聊天搜索
    clearCurrentChatSearch() {
      this.currentSearchKeyword = "";
      this.showSearchResults = false;
      this.currentSearchResults = [];
      this.currentSearchIndex = -1;
    },

    // 导航到上一个搜索结果
    navigateToPreviousResult() {
      if (this.currentSearchIndex > 0) {
        this.currentSearchIndex--;
        this.scrollToSearchResult(this.currentSearchResults[this.currentSearchIndex]);
      }
    },

    // 导航到下一个搜索结果
    navigateToNextResult() {
      if (this.currentSearchIndex < this.currentSearchResults.length - 1) {
        this.currentSearchIndex++;
        this.scrollToSearchResult(this.currentSearchResults[this.currentSearchIndex]);
      }
    },

    // 滚动到搜索结果
    scrollToSearchResult(result) {
      if (!result || !this.$refs.messagesContainer) return;

      // 找到对应的消息元素
      const messageElement = this.$refs.messagesContainer.querySelector(`[data-message-id="${result.id}"]`);
      if (messageElement) {
        // 滚动到消息位置
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // 添加高亮效果
        messageElement.classList.add('search-highlight');
        setTimeout(() => {
          messageElement.classList.remove('search-highlight');
        }, 2000);
      }
    },

    // 处理从全局搜索跳转过来的消息定位
    handleLocateMessage({ messageId, keyword }) {
      if (!messageId) return;

      // 设置搜索关键词
      this.currentSearchKeyword = keyword || "";
      
      // 查找消息
      const messages = this.messages;
      const message = messages.find(msg => msg.id === messageId);
      
      if (message) {
        // 执行搜索
        this.performCurrentChatSearch();
        
        // 找到对应的结果索引
        const resultIndex = this.currentSearchResults.findIndex(result => result.id === messageId);
        if (resultIndex !== -1) {
          this.currentSearchIndex = resultIndex;
          this.scrollToSearchResult(message);
        }
      }
    },
    // 处理键盘事件 - Enter 发送消息，Shift+Enter 换行
    handleKeyDownNative(event) {
      console.log("handleKeyDownNative", {
        key: event && event.key,
        keyCode: event && event.keyCode,
        shiftKey: event && event.shiftKey,
        ctrlKey: event && event.ctrlKey,
        messageText: this.messageText,
        trimmed: this.messageText && this.messageText.trim(),
      });
      
      // 检查是否按下了 Enter 键（keyCode 13）
      if (event.keyCode === 13 || event.key === 'Enter') {
        // 如果是 Shift+Enter，允许换行（不阻止默认行为）
        if (event.shiftKey) {
          console.log("handleKeyDownNative - Shift+Enter detected, allowing line break");
          return;
        }
        
        // 纯 Enter 键 - 发送消息
        console.log("handleKeyDownNative - Enter detected, sending message");
        if (this.messageText.trim()) {
          event.preventDefault();
          event.stopPropagation();
          this.sendMessage();
          return false;
        } else {
          // 消息为空，阻止换行
          event.preventDefault();
          return false;
        }
      }
    },

    // ========== 黑名单相关方法 ==========

    // 拉黑用户
    handleBlacklistUser() {
      if (!this.activeConversation) {
        this.$message.warning("请先选择一个对话");
        return;
      }

      const userId = this.activeConversation.customerId;
      const userName = this.activeConversation.customerName;

      this.$confirm(
        `确定要拉黑用户 "${userName}" (${userId}) 吗？`,
        "提示",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(async () => {
          try {
            const response = await this.$http.post("/api/blacklist", {
              userId: userId,
              userName: userName,
              ipAddress: this.activeConversation.customerIp || "",
              reason: "客服拉黑",
            });

            if (response.data && response.data.code === 200) {
              this.$message.success("用户已拉黑");
              // 更新当前对话的黑名单状态
              if (this.activeConversation) {
                this.activeConversation.isBlacklisted = true;
              }
              // 刷新左边对话列表
              await this.$store.dispatch("loadConversations");
            } else {
              throw new Error(response.data.msg || "拉黑失败");
            }
          } catch (error) {
            this.$message.error("拉黑用户失败");
          }
        })
        .catch(() => {
          // 用户取消操作
        });
    },

    // 解除拉黑用户
    handleUnblacklistUser() {
      if (!this.activeConversation) {
        this.$message.warning("请先选择一个对话");
        return;
      }

      const userId = this.activeConversation.customerId;
      const userName = this.activeConversation.customerName;

      this.$confirm(
        `确定要解除拉黑用户 "${userName}" (${userId}) 吗？`,
        "提示",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(async () => {
          try {
            // 直接调用删除接口，使用userId
            const deleteResponse = await this.$http.put(
              `/api/blacklist/${userId}`
            );

            if (deleteResponse.data && deleteResponse.data.code === 200) {
              this.$message.success("黑名单已解除");
              // 更新当前对话的黑名单状态
              if (this.activeConversation) {
                this.activeConversation.isBlacklisted = false;
              }
              // 刷新左边对话列表
              await this.$store.dispatch("loadConversations");
            } else {
              throw new Error(
                deleteResponse.data.msg || "解除黑名单失败"
              );
            }
          } catch (error) {
            this.$message.error("解除黑名单失败");
          }
        })
        .catch(() => {
          // 用户取消操作
        });
    },

    // 查看黑名单
    async handleViewBlacklist() {
      this.blacklistDialogVisible = true;
      this.blacklistPageNum = 1;
      await this.loadBlacklistData();
    },

    // 加载黑名单数据
    async loadBlacklistData() {
      this.blacklistLoading = true;
      try {
        const response = await this.$http.get("/api/blacklist", {
          params: {
            pageNum: this.blacklistPageNum,
            pageSize: this.blacklistPageSize,
          },
        });
        
        if (response.data && response.data.code === 200) {
          let data;
          if (isBase64String(response.data.data)) {
            const decrypted = decryptAES(response.data.data);
            // 如果是字符串则需要JSON.parse，如果已经是对象则直接使用
            if (typeof decrypted === 'string') {
              data = JSON.parse(decrypted);
            } else {
              data = decrypted;
            }
          } else {
            data = response.data.data;
          }
          
          this.blacklistData = data.records || [];
          this.blacklistTotal = parseInt(data.total) || 0;
        } else {
          throw new Error(response.data.msg || "获取黑名单失败");
        }
      } catch (error) {
        this.$message.error("加载黑名单失败");
      } finally {
        this.blacklistLoading = false;
      }
    },

    // 从黑名单移除
    removeFromBlacklist(id) {
      this.$confirm("确定要解除这条黑名单记录吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(async () => {
          try {
            const response = await this.$http.put(`/api/blacklist/${id}`);

            if (response.data && response.data.code === 200) {
              this.$message.success("黑名单已解除");
              // 重新加载黑名单
              await this.loadBlacklistData();
              // 刷新左边对话列表
              await this.$store.dispatch("loadConversations");
            } else {
              throw new Error(response.data.msg || "解除失败");
            }
          } catch (error) {
            this.$message.error("解除黑名单失败");
          }
        })
        .catch(() => {
          // 用户取消操作
        });
    },

    // 黑名单分页改变
    handleBlacklistPageChange(val) {
      this.blacklistPageNum = val;
      this.loadBlacklistData();
    },

    // 黑名单每页大小改变
    handleBlacklistPageSizeChange(val) {
      this.blacklistPageSize = val;
      this.blacklistPageNum = 1;
      this.loadBlacklistData();
    },
  },
  mounted() {
    // 监听全局搜索的定位消息事件
    this.$parent.$on('locate-message', this.handleLocateMessage);
    
    // 添加滚动事件监听
    this.addScrollListener();

    // 初始化 AudioContext（绕过浏览器用户交互限制）
    this.initAudioContext();
  },
  beforeDestroy() {
    // 移除事件监听器
    this.$parent.$off('locate-message', this.handleLocateMessage);
    
    // 移除滚动事件监听器
    this.removeScrollListener();
  },
};
</script>

<style scoped>
.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f7f7f7;
}

.chat-header {
  height: 60px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  background: white;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
}

.sidebar-toggle {
  margin-right: 15px;
}

.conversation-title {
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  text-align: center;
}

.logout-btn {
  margin-left: auto;
}

/* 当前聊天搜索样式 */
.current-chat-search {
  display: flex;
  align-items: center;
  margin-right: 10px;
}

.current-chat-search :deep(.el-input-group__append) {
  background-color: #409eff;
  border-color: #409eff;
  color: white;
}

.current-chat-search :deep(.el-input-group__append:hover) {
  background-color: #66b1ff;
  border-color: #66b1ff;
}

.search-navigation {
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: #f5f7fa;
  border-radius: 4px;
  padding: 4px 8px;
  border: 1px solid #ebeef5;
}

.search-count {
  font-size: 12px;
  color: #606266;
  margin-right: 4px;
  min-width: 40px;
  text-align: center;
}

.search-navigation .el-button {
  padding: 4px;
  min-width: 24px;
  height: 24px;
}

.search-navigation .el-button.is-disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}

.search-navigation .el-button:not(.is-disabled):hover {
  background-color: #f0f7ff;
  color: #409eff;
}

/* 搜索高亮效果 */
.search-highlight {
  animation: highlight-pulse 2s ease-in-out;
  background-color: #ffd54f !important;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(255, 213, 79, 0.3);
}

@keyframes highlight-pulse {
  0% {
    background-color: #ffd54f;
    box-shadow: 0 0 0 2px rgba(255, 213, 79, 0.3);
  }
  50% {
    background-color: #fff176;
    box-shadow: 0 0 0 4px rgba(255, 213, 79, 0.5);
  }
  100% {
    background-color: #ffd54f;
    box-shadow: 0 0 0 2px rgba(255, 213, 79, 0.3);
  }
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.no-conversation {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-more-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #909399;
  font-size: 14px;
}

.loading-more-indicator i {
  margin-right: 8px;
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 输入区域样式 */
.message-input-area {
  background: white;
  border-top: 1px solid #e8e8e8;
  flex-shrink: 0;
}

.input-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 16px 24px;
  position: relative;
}

.input-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.tool-button {
  color: #666;
  border-radius: 8px;
  transition: all 0.2s ease;
  font-size: 16px;
  padding: 8px;
}

.tool-button:hover {
  color: #409eff;
  background-color: #f0f7ff;
  transform: scale(1.05);
}

.input-wrapper {
  position: relative;
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.message-input {
  flex: 1;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.message-input :deep(.el-textarea__inner) {
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: #fafbfc;
  resize: none;
  line-height: 1.5;
  max-height: 200px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.message-input :deep(.el-textarea__inner):focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
  background: white;
}

.message-input :deep(.el-textarea__inner):hover {
  border-color: #c0c4cc;
}

.send-button {
  border-radius: 50%;
  width: 40px;
  height: 40px;
  padding: 0;
  min-width: auto;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  flex-shrink: 0;
  margin-bottom: 4px;
}

.send-button:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.send-button.is-disabled {
  background: #f5f7fa;
  color: #c0c4cc;
  box-shadow: none;
  transform: scale(1);
}

.send-button.is-disabled:hover {
  transform: scale(1);
  box-shadow: none;
}

.send-button i {
  font-size: 16px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .input-container {
    padding: 12px 16px;
  }

  .input-tools {
    margin-bottom: 10px;
    gap: 6px;
  }

  .tool-button {
    padding: 6px;
    font-size: 14px;
  }

  .message-input :deep(.el-textarea__inner) {
    font-size: 13px;
    padding: 10px 12px;
  }

  .send-button {
    width: 36px;
    height: 36px;
    margin-bottom: 2px;
  }

  .send-button i {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .input-container {
    padding: 10px 12px;
  }

  .input-tools {
    margin-bottom: 8px;
    gap: 4px;
  }

  .tool-button {
    padding: 5px;
    font-size: 13px;
  }

  .message-input :deep(.el-textarea__inner) {
    font-size: 12px;
    padding: 8px 10px;
  }

  .send-button {
    width: 32px;
    height: 32px;
    margin-bottom: 2px;
  }

  .send-button i {
    font-size: 12px;
  }
}
</style>
